import { useCallback, useMemo, useState } from 'react'

import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { ISourceMap, TableFiltersProps, UseTableFiltersParams } from './types'

const defaultIdAccessor = (record: any): string => record.id

interface UseTableFiltersResult<T> {
  filterProps: TableFiltersProps
  applyFilters: (records: T[]) => T[]
  hasActiveFilters: boolean
  setAppliedFilters: React.Dispatch<
    React.SetStateAction<{ [facetKey: string]: Set<string> }>
  >
}

export const useTableFilters = <T>({
  records,
  columns,
  idAccessor = defaultIdAccessor,
}: UseTableFiltersParams<T>): UseTableFiltersResult<T> => {
  const [appliedFilters, setAppliedFilters] = useState<{
    [facetKey: string]: Set<string>
  }>({})
  const [searchKey, setSearchKey] = useState<string>('')

  const facets = useMemo(
    () =>
      columns
        .filter(col => col.isFacetable)
        .map(col => ({
          key: col.dataKey,
          label: col.facetLabel || col.label || col.dataKey,
          accessorFn: col.facetAccessorFn || col.accessorFn,
          displayFormatter: col.facetDisplayFormatter || col.displayFormatter,
          isHiddenInFilterUI: col.isHiddenInFilterUI,
          hierarchy: col.hierarchy,
        })),
    [columns],
  )

  const searchableColumns = useMemo(
    () => columns.filter(col => col.isSearchable !== false),
    [columns],
  )

  const sourceMapByFacet = useMemo(() => {
    const result: { [facetKey: string]: ISourceMap } = {}

    facets.forEach(facet => {
      const map: ISourceMap = {}

      records.forEach(record => {
        const recordId = idAccessor(record)
        const raw = facet.accessorFn(record)
        let optionIds: string[]

        if (Array.isArray(raw)) {
          optionIds = raw.length
            ? raw.map(v => String(v))
            : [UNASSIGNED_FILTER_VALUE]
        } else if (raw != null && raw !== '') {
          optionIds = [String(raw)]
        } else {
          optionIds = [UNASSIGNED_FILTER_VALUE]
        }

        optionIds.forEach(optionId => {
          if (!map[optionId]) map[optionId] = []
          map[optionId].push(recordId)
        })
      })

      result[facet.key] = map
    })

    return result
  }, [records, facets, idAccessor])

  const handleFilterApply = useCallback(
    (facetKey: string, selectedIds: string[]) => {
      setAppliedFilters(prev => {
        const next = { ...prev }
        if (selectedIds.length === 0) {
          delete next[facetKey]
        } else {
          next[facetKey] = new Set(selectedIds)
        }
        return next
      })
    },
    [],
  )

  const handleSearchChange = useCallback((newSearchKey: string) => {
    setSearchKey(newSearchKey)
  }, [])

  const handleResetAll = useCallback(() => {
    setAppliedFilters({})
    setSearchKey('')
  }, [])

  const applyFilters = useCallback(
    (recordsToFilter: T[]) => {
      let filtered = recordsToFilter

      const activeFilterKeys = Object.keys(appliedFilters).filter(
        key => appliedFilters[key].size > 0,
      )

      if (activeFilterKeys.length > 0) {
        const allowedByFacet: { [facetKey: string]: Set<string> } = {}
        activeFilterKeys.forEach(facetKey => {
          const sourceMap = sourceMapByFacet[facetKey]
          const allowedIds = appliedFilters[facetKey]
          if (!sourceMap || !allowedIds) return
          const allowed = new Set<string>()
          allowedIds.forEach(optionId => {
            const recordIds = sourceMap[optionId]
            if (recordIds) recordIds.forEach(id => allowed.add(id))
          })
          allowedByFacet[facetKey] = allowed
        })

        filtered = filtered.filter(record => {
          const recordId = idAccessor(record)
          return activeFilterKeys.every(facetKey => {
            const allowed = allowedByFacet[facetKey]
            return !allowed || allowed.has(recordId)
          })
        })
      }

      if (searchKey) {
        const lowerSearch = searchKey.toLowerCase()
        filtered = filtered.filter(record => {
          return searchableColumns.some(column => {
            if (column.searchAccessorFn) {
              const searchValue = column.searchAccessorFn(record)
              if (searchValue == null) return false

              const searchValues = Array.isArray(searchValue)
                ? searchValue.map(value => String(value))
                : [String(searchValue)]

              return searchValues.some(value =>
                value.toLowerCase().includes(lowerSearch),
              )
            }

            const rawValue = column.accessorFn(record)
            if (rawValue == null) return false

            const displayValue = column.displayFormatter
              ? column.displayFormatter(rawValue)
              : String(rawValue)

            return displayValue.toLowerCase().includes(lowerSearch)
          })
        })
      }

      return filtered
    },
    [
      appliedFilters,
      sourceMapByFacet,
      idAccessor,
      searchKey,
      searchableColumns,
    ],
  )

  // Exclude stale filter keys that no longer match any known facet
  // (e.g., when a resource property is archived or deleted).
  const effectiveAppliedFilters = useMemo(() => {
    const result: { [facetKey: string]: Set<string> } = {}
    for (const [key, values] of Object.entries(appliedFilters)) {
      if (values.size > 0 && sourceMapByFacet[key]) {
        result[key] = values
      }
    }
    return result
  }, [appliedFilters, sourceMapByFacet])

  const hasActiveFilters = useMemo(
    () =>
      Object.keys(effectiveAppliedFilters).some(
        key => effectiveAppliedFilters[key].size > 0,
      ) || searchKey.length > 0,
    [effectiveAppliedFilters, searchKey],
  )

  const filterProps = useMemo(
    () => ({
      facets,
      sourceMapByFacet,
      appliedFilters: effectiveAppliedFilters,
      onFilterApply: handleFilterApply,
      onResetAll: handleResetAll,
      availableRecords: records,
      searchKey,
      onSearchChange: handleSearchChange,
    }),
    [
      facets,
      sourceMapByFacet,
      effectiveAppliedFilters,
      handleFilterApply,
      handleResetAll,
      records,
      searchKey,
      handleSearchChange,
    ],
  )

  return {
    filterProps,
    applyFilters,
    hasActiveFilters,
    setAppliedFilters,
  }
}
